import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import { useId } from '../hooks/useId'
import { RadioContext } from './RadioContext'
import type {
  RadioInputColor,
  RadioInputSize,
  StateDefiniton,
} from './RadioContext'

interface RadioItemsProps {
  color?: RadioInputColor
  defaultValue?: string | number
  disabled?: boolean
  error?: boolean
  helperText?: string
  name?: string
  onChange?: (event: {
    target: { name: string; value: string | number }
  }) => void
  size?: RadioInputSize
}

type ItemsContextState = Omit<StateDefiniton, 'direction'>

function Items({
  children,
  color,
  disabled,
  defaultValue = '',
  error = false,
  helperText = '',
  name,
  onChange,
  size,
}: PropsWithChildren<RadioItemsProps>): JSX.Element {
  const id = useId()
  const [state, setState]: [
    ItemsContextState,
    Dispatch<SetStateAction<ItemsContextState>>,
  ] = useState({
    color,
    disabled,
    name: name || `coconut-radio-group-${id}`,
    size,
    value: defaultValue,
  })

  useEffect(() => {
    if (disabled !== state.disabled) {
      setState((prevState) => ({ ...prevState, disabled }))
    }
  }, [disabled, state.disabled])

  const setValue = (value: string | number) => {
    setState((prevState) => ({ ...prevState, value }))

    if (onChange) {
      onChange({ target: { name: state.name, value: value } })
    }
  }

  const setLabelledBy = (id: string) =>
    setState((prevState) => ({ ...prevState, labelledBy: id }))

  return (
    <RadioContext.Provider value={[state, setValue, setLabelledBy]}>
      <>
        {children}
        {helperText ? (
          <span
            aria-live={error ? 'assertive' : 'off'}
            className={clsx(
              'text-xs',
              error ? 'text-error-main' : 'text-black-60',
            )}
          >
            {helperText}
          </span>
        ) : null}
      </>
    </RadioContext.Provider>
  )
}

export default Items
